/**
 * Converts PromiseManyArray to plain array while maintaining reactivity
 *
 * In Ember 5.0, PromiseManyArray doesn't expose array methods like .filter(),
 * .map(), .reduce(), etc. directly. This utility properly converts PromiseManyArray
 * to a plain array by accessing the .content property, which maintains Ember's
 * reactivity tracking.
 *
 * @param {*} maybeArray - A value that might be a PromiseManyArray, array, or other type
 * @returns {Array} - A plain JavaScript array
 *
 * @example
 * // In a component:
 * import { getArrayFromPromiseManyArray } from 'bullitttime-embercomponents/utils/promise-many-array';
 *
 * const entries = getArrayFromPromiseManyArray(this.args.entries);
 * const filtered = entries.filter(e => e.status === 'active');
 */
export function getArrayFromPromiseManyArray(maybeArray) {
	if (!maybeArray) {
		return [];
	}

	// Handle EmberData PromiseManyArray - access .content for reactivity
	// This is the key to maintaining Ember's reactivity tracking
	if (typeof maybeArray === 'object' &&
		typeof maybeArray.then === 'function' &&
		'content' in maybeArray) {
		const content = maybeArray.content;

		if (!content) {
			return [];
		}

		// Convert content to array using spread operator
		if (typeof content[Symbol.iterator] === 'function') {
			return [...content];
		}

		// If content is already an array, use it
		if (Array.isArray(content)) {
			return content;
		}

		return [];
	}

	// Handle regular iterable objects
	if (typeof maybeArray[Symbol.iterator] === 'function') {
		return [...maybeArray];
	}

	// Already an array
	if (Array.isArray(maybeArray)) {
		return maybeArray;
	}

	return [];
}
